/**
 * Namespaces API
 *
 * Fetch and manage user namespaces with a small localStorage cache to reduce
 * duplicate requests. Also exposes CRUD and membership utilities.
 */
import { get, post, put, del } from '../core';
import { auth } from '../../firebase/firebase';

// Cache keys
const NAMESPACE_CACHE_KEY = 'namespaces_cache';
const NAMESPACE_CACHE_TIMESTAMP_KEY = 'namespaces_cache_timestamp';
const NAMESPACE_CACHE_TTL = 5 * 60 * 1000; // 5 minutes cache TTL

/**
 * Clear the namespace cache (useful when user changes)
 */
export function clearNamespaceCache() {
  localStorage.removeItem(NAMESPACE_CACHE_KEY);
  localStorage.removeItem(NAMESPACE_CACHE_TIMESTAMP_KEY);
  console.log('API: Namespace cache cleared');
}

/**
 * Fetch user namespaces with built-in caching to prevent duplicate API calls
 * @param {boolean} forceRefresh - Bypass cache and fetch fresh data
 * @returns {Promise<Array>} - The list of namespaces
 */
export async function getNamespaces(options = {}) {
  const { forceRefresh = false } = options;
  
  // Check if user is authenticated
  if (!auth.currentUser) {
    throw new Error('User not authenticated');
  }

  const now = Date.now();
  const cachedTimestamp = parseInt(localStorage.getItem(NAMESPACE_CACHE_TIMESTAMP_KEY) || '0');
  const cachedData = localStorage.getItem(NAMESPACE_CACHE_KEY);
  
  // Use cache if available and not expired and not forcing refresh
  if (!forceRefresh && cachedData && (now - cachedTimestamp) < NAMESPACE_CACHE_TTL) {
    console.log('API: Using cached namespaces data', { 
      age: Math.round((now - cachedTimestamp) / 1000) + 's', 
      expires: Math.round((NAMESPACE_CACHE_TTL - (now - cachedTimestamp)) / 1000) + 's remaining'
    });
    try {
      return JSON.parse(cachedData);
    } catch (e) {
      console.error('API: Error parsing cached namespace data', e);
      // Continue to fetch fresh data if parsing failed
    }
  }

  // If cache is invalid or we're forcing refresh, fetch from API
  console.log('API: Fetching fresh namespaces data from server');
  try {
    const data = await get('/namespaces/my');
    
    // Save to cache
    localStorage.setItem(NAMESPACE_CACHE_KEY, JSON.stringify(data));
    localStorage.setItem(NAMESPACE_CACHE_TIMESTAMP_KEY, now.toString());
    
    return data;
  } catch (error) {
    console.error('API: Error fetching namespaces:', error);
    
    // If we have cached data, return it as fallback even if expired
    if (cachedData) {
      console.log('API: Using expired cache as fallback due to fetch error');
      try {
        return JSON.parse(cachedData);
      } catch (e) {
        console.error('API: Error parsing fallback cached data', e);
      }
    }
    
    // Re-throw the original error if no cache available or parsing failed
    throw error;
  }
}

/**
 * Fetches a single namespace by ID
 * @param {string} namespaceId - The ID of the namespace
 * @param {Object} options - Additional options for the request
 * @returns {Promise<Object>} - The namespace data
 */
export function getNamespace(namespaceId, options = {}) {
  return get(`/namespaces/${namespaceId}`, options);
}

/**
 * Creates a new namespace
 * @param {Object} namespaceData - The namespace data to create
 * @returns {Promise<Object>} - The created namespace
 */
export function createNamespace(namespaceData) {
  return post('/namespaces', namespaceData);
}

/**
 * Updates an existing namespace
 * @param {string} namespaceId - The ID of the namespace to update
 * @param {Object} namespaceData - The updated namespace data
 * @returns {Promise<Object>} - The updated namespace
 */
export function updateNamespace(namespaceId, namespaceData) {
  return put(`/namespaces/${namespaceId}`, namespaceData);
}

/**
 * Deletes a namespace
 * @param {string} namespaceId - The ID of the namespace to delete
 * @returns {Promise<Object>} - The response
 */
export function deleteNamespace(namespaceId) {
  return del(`/namespaces/${namespaceId}`);
}

/**
 * Gets namespace analytics
 * @param {string} namespaceId - The ID of the namespace
 * @param {Object} queryParams - Query parameters for the analytics request
 * @returns {Promise<Object>} - The namespace analytics
 */
export function getNamespaceAnalytics(namespaceId, queryParams = {}) {
  return get(`/namespaces/${namespaceId}/analytics`, { params: queryParams });
}

/**
 * Gets namespace members
 * @param {string} namespaceId - The ID of the namespace
 * @returns {Promise<Array>} - The list of namespace members
 */
export function getNamespaceMembers(namespaceId) {
  return get(`/namespaces/${namespaceId}/members`);
}

/**
 * Adds a member to the namespace
 * @param {string} namespaceId - The ID of the namespace
 * @param {Object} memberData - The member data to add
 * @returns {Promise<Object>} - The added member
 */
export function addNamespaceMember(namespaceId, memberData) {
  return post(`/namespaces/${namespaceId}/members`, memberData);
}

/**
 * Updates a namespace member's permissions
 * @param {string} namespaceId - The ID of the namespace
 * @param {string} memberId - The ID of the member
 * @param {Object} permissions - The updated permissions
 * @returns {Promise<Object>} - The updated member
 */
export function updateNamespaceMember(namespaceId, memberId, permissions) {
  return put(`/namespaces/${namespaceId}/members/${memberId}`, permissions);
}

/**
 * Removes a member from the namespace
 * @param {string} namespaceId - The ID of the namespace
 * @param {string} memberId - The ID of the member to remove
 * @returns {Promise<Object>} - The response
 */
export function removeNamespaceMember(namespaceId, memberId) {
  return del(`/namespaces/${namespaceId}/members/${memberId}`);
}

export default {
  getNamespaces,
  getNamespace,
  createNamespace,
  updateNamespace,
  deleteNamespace,
  getNamespaceAnalytics,
  getNamespaceMembers,
  addNamespaceMember,
  updateNamespaceMember,
  removeNamespaceMember,
  clearNamespaceCache
}; 